import { useState, useEffect } from 'react';
import { useCurrentShift, useClockIn, useClockOut } from '../../hooks/useCurrentShift.js';
import { useJobs } from '../../hooks/useJobs.js';
import { useJhaToday } from '../../hooks/useJhaToday.js';
import { useError } from '../shared/ErrorBanner.jsx';
import { getPosition } from '../../lib/geo.js';
import { formatDuration } from '../../lib/format.js';

export default function ClockWidget() {
  const { data: open } = useCurrentShift();
  const { data: jobs = [] } = useJobs('active');
  const { data: jha } = useJhaToday();
  const clockIn = useClockIn();
  const clockOut = useClockOut();
  const { show } = useError();

  const [jobId, setJobId] = useState('');
  const [tick, setTick] = useState(Date.now());

  // Re-render the running timer every second while clocked in.
  useEffect(() => {
    if (!open) return;
    const id = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [open]);

  const busy = clockIn.isPending || clockOut.isPending;
  const jhaSubmitted = !!jha?.submitted;
  const elapsed = open ? tick - open.clock_in_at : 0;

  const onClockIn = async () => {
    try {
      const job = jobs.find(j => j.id === jobId);
      const needGeo = !!(job && job.site_lat != null && job.site_lng != null);
      const pos = await getPosition({ required: needGeo });
      await clockIn.mutateAsync({
        job_id: jobId || null,
        lat: pos.lat, lng: pos.lng, accuracy_m: pos.accuracy_m,
      });
    } catch (err) {
      if (err.status === 412 && err.reason === 'jha_required') {
        show('Submit today\'s JHA first.');
      } else if (err.status === 422 && err.body?.distance_m != null) {
        show(`You're ${err.body.distance_m}m from the site (allowed: ${err.body.allowed_m}m).`);
      } else {
        show(err.message);
      }
    }
  };

  const onClockOut = async () => {
    try {
      const pos = await getPosition({ required: false });
      await clockOut.mutateAsync({ lat: pos.lat, lng: pos.lng });
    } catch (err) {
      show(err.message);
    }
  };

  return (
    <div className="panel">
      <h3 className="panel-title">
        <span><i className="ph ph-timer" /> Time clock</span>
      </h3>
      <div className="clock-widget">
        <div className={'clock-state' + (open ? ' on' : '')}>
          <span className="dot" />
          {open ? 'On the clock' : 'Off the clock'}
        </div>
        <div className="clock-time">{open ? formatDuration(elapsed) : '00:00:00'}</div>
        {open && open.job_name && <div className="clock-job">{open.job_name}</div>}

        {!open && (
          <div className="field" style={{ textAlign: 'left' }}>
            <label className="field-label">Job (optional)</label>
            <select className="field-select"
              value={jobId} onChange={e => setJobId(e.target.value)}
              disabled={busy}>
              <option value="">— none —</option>
              {jobs.map(j => (
                <option key={j.id} value={j.id}>
                  {j.name}{j.client ? ` · ${j.client}` : ''}
                  {j.site_lat != null ? ' · GEO' : ''}
                </option>
              ))}
            </select>
          </div>
        )}

        {!open ? (
          <button
            className="clock-action"
            disabled={busy || !jhaSubmitted}
            onClick={onClockIn}
            title={!jhaSubmitted ? 'Submit today\'s JHA first' : ''}
          >
            {busy ? 'Working…' : (jhaSubmitted ? 'Clock In' : 'JHA required')}
          </button>
        ) : (
          <button className="clock-action danger" disabled={busy} onClick={onClockOut}>
            {busy ? 'Working…' : 'Clock Out'}
          </button>
        )}

        <div className="clock-meta">
          {open
            ? `Started ${new Date(open.clock_in_at).toLocaleTimeString()}`
            : (jhaSubmitted ? 'Pick a job site, then clock in.' : 'Complete your daily JHA above ↑')}
        </div>
      </div>
    </div>
  );
}
